<template>
  <component
    :is="tag"
    :type="tag === 'button' ? type : undefined"
    :to="tag === 'NuxtLink' ? to : undefined"
    :href="tag === 'a' ? href : undefined"
    :class="buttonClasses"
    :disabled="disabled"
  >
    <slot />
  </component>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  variant: {
    type: String,
    default: 'primary',
    validator: (value) => ['primary', 'secondary', 'outline', 'ghost', 'text'].includes(value)
  },
  size: {
    type: String,
    default: 'md',
    validator: (value) => ['sm', 'md', 'lg'].includes(value)
  },
  type: {
    type: String,
    default: 'button',
    validator: (value) => ['button', 'submit', 'reset'].includes(value)
  },
  disabled: {
    type: Boolean,
    default: false
  },
  fullWidth: {
    type: Boolean,
    default: false
  },
  to: {
    type: String,
    default: undefined
  },
  href: {
    type: String,
    default: undefined
  },
  class: {
    type: String,
    default: ''
  }
})

const tag = computed(() => {
  if (props.to) return 'NuxtLink'
  if (props.href) return 'a'
  return 'button'
})

const buttonClasses = computed(() => {
  const baseClasses = [
    'inline-flex',
    'items-center',
    'justify-center',
    'font-inter',
    'font-semibold',
    'rounded-full',
    'transition-all',
    'duration-250',
    'focus:outline-none',
    'focus:ring-2',
    'focus:ring-offset-2',
    'disabled:opacity-50',
    'disabled:cursor-not-allowed',
    'disabled:pointer-events-none'
  ]

  // Variant styles
  const variantClasses = {
    primary: [
      'bg-brand',
      'text-pruta-green',
      'hover:bg-brand-400',
      'focus:ring-brand',
      'border',
      'border-transparent'
    ],
    secondary: [
      'bg-pruta-green',
      'text-brand',
      'hover:bg-pruta-green-600',
      'focus:ring-pruta-green',
      'border',
      'border-transparent'
    ],
    outline: [
      'bg-transparent',
      'text-brand',
      'border',
      'border-brand',
      'hover:bg-brand',
      'hover:text-pruta-green',
      'focus:ring-brand'
    ],
    ghost: [
      'bg-transparent',
      'text-brand',
      'hover:bg-brand/10',
      'focus:ring-brand',
      'border',
      'border-transparent'
    ],
    text: [
      'bg-transparent',
      'text-brand',
      'hover:text-brand-400',
      'focus:ring-brand',
      'border-none',
      'px-0'
    ]
  }

  // Size styles
  const sizeClasses = {
    sm: ['px-4', 'py-2', 'text-xs'],
    md: ['px-6', 'py-3', 'text-sm'],
    lg: ['px-8', 'py-3', 'text-md']
  }

  // Width class
  const widthClass = props.fullWidth ? 'w-full' : ''

  return [
    ...baseClasses,
    ...variantClasses[props.variant],
    ...sizeClasses[props.size],
    widthClass,
    props.class
  ].filter(Boolean).join(' ')
})
</script>
